import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";

// components
import Filter from "components/molecules/Filter/Filter";
import FilterContainer from "components/molecules/FilterContainer/FilterContainer";
import Sidebar from "components/molecules/Sidebar";

// hooks
import { useDebounce } from "hooks/useDebounce";
import useFilters, { emptyFilters } from "recoilStore/useFilters";

import { buildingsFilterKeyToInfoMap } from "./FilterSidebar.constants";

// helpers

import { buildIsFieldActiveMap, cloneFilters, getFilterNameFromKey, removeFilter } from "./FilterSidebar.helpers";

// styling
import FiltersSidebarFooter from "./Filter/FiltersSidebarFooter";
import "./index.scss";

interface FiltersSidebarProps {
    closeSidebar: () => void;
    sidebarOpen: boolean;
    currentActiveTab?: ReportTab;
}

function FiltersSidebar({ closeSidebar, sidebarOpen, currentActiveTab }: FiltersSidebarProps) {
    // Hook
    const { setFilters, filters } = useFilters();
    const [localFilters, setLocalFilters] = useState(cloneFilters(filters));
    const [isFieldActiveMap, setIsFieldActiveMap] = useState(buildIsFieldActiveMap(filters));

    const debouncedSetLocalFilters = useDebounce(setLocalFilters, 300);

    const numberOfLocalFilters = useMemo(() => {
        const { numericFilters, stringFilters, dateFilters, dataCoverageFilter } = localFilters;
        return numericFilters.length + stringFilters.length + dateFilters.length + dataCoverageFilter.length;
    }, [localFilters]);

    useEffect(() => {
        setLocalFilters(cloneFilters(filters));
        setIsFieldActiveMap(buildIsFieldActiveMap(filters));
    }, [filters]);

    const handleApplyFilters = async () => {
        await Promise.resolve(setFilters(localFilters));
        const posthogSimplifiedFilters = {
            ...localFilters,
            numericFilters: localFilters.numericFilters.map(({ field }) => field),
            stringFilters: localFilters.stringFilters.map(({ field }) => field),
        };

        posthog.capture("Filters applied", posthogSimplifiedFilters);
        closeSidebar();
        setIsFieldActiveMap(buildIsFieldActiveMap(localFilters));
    };

    const handleClearAll = () => {
        setLocalFilters(emptyFilters);
        posthog.capture("Filters cleared");
        setIsFieldActiveMap(buildIsFieldActiveMap(emptyFilters));
    };

    const handleCancel = () => {
        posthog.capture("Filters closed");
        setLocalFilters({ ...filters });
        closeSidebar();
        setIsFieldActiveMap(buildIsFieldActiveMap(filters));
    };

    const handleClearFilter = (filterField: string) => setLocalFilters((prev) => removeFilter(prev, filterField));

    const handleSelectFilter = (checked: boolean, key: string) => {
        setIsFieldActiveMap((prev) => {
            const copy = new Map(prev);
            copy.set(key, checked);
            return copy;
        });
    };

    return (
        <Sidebar
            onCancel={handleCancel}
            sidebarOpen={sidebarOpen}
            title={`Filters ${numberOfLocalFilters > 0 ? `(${numberOfLocalFilters})` : ""}`}
        >
            <div className="filters-sidebar__body">
                {Object.keys(buildingsFilterKeyToInfoMap).map((key) => {
                    const { type, field } = buildingsFilterKeyToInfoMap[key];
                    return (
                        <FilterContainer
                            name={getFilterNameFromKey(key)}
                            checked={!!isFieldActiveMap.get(field)}
                            setChecked={(checked) => handleSelectFilter(checked, field)}
                            key={field}
                            handleUncheck={() => handleClearFilter(field)}
                        >
                            <Filter
                                filters={localFilters}
                                setFilters={debouncedSetLocalFilters}
                                filterKey={key}
                                filterField={field}
                                type={type}
                            />
                        </FilterContainer>
                    );
                })}
            </div>
            <FiltersSidebarFooter
                onClickApply={handleApplyFilters}
                onClickCancel={handleCancel}
                onClickClearAll={handleClearAll}
                localFilters={localFilters}
                isLoading={false}
            />
        </Sidebar>
    );
}

export default FiltersSidebar;
